import { createClient, type SupabaseClient } from '@supabase/supabase-js'

/**
 * Browser Supabase client.
 *
 * The browser holds ONLY the publishable (anon) key. That is safe because RLS is enabled and
 * forced on every table (migrations C0-C18), so this key grants exactly what the policies allow
 * and nothing more. The service-role key bypasses RLS entirely and must never appear in a build
 * variable, a bundle, or a repository secret (CLAUDE.md §2 never 1).
 */

const url = import.meta.env.VITE_SUPABASE_URL
const anonKey = import.meta.env.VITE_SUPABASE_ANON_KEY

export type ConfigStatus = { ok: true; url: string } | { ok: false; missing: string[] }

export function configStatus(): ConfigStatus {
  const missing: string[] = []
  if (!url) missing.push('VITE_SUPABASE_URL')
  if (!anonKey) missing.push('VITE_SUPABASE_ANON_KEY')
  return missing.length === 0 ? { ok: true, url: url as string } : { ok: false, missing }
}

let client: SupabaseClient | null = null

export function getClient(): SupabaseClient | null {
  if (!url || !anonKey) return null
  client ??= createClient(url, anonKey, {
    auth: { persistSession: true, autoRefreshToken: true },
  })
  return client
}

/** A row count the anon key is allowed to see. Zero is the expected, correct answer. */
export type Probe = {
  table: string
  reachable: boolean
  rows: number
  note: string
}

/**
 * Probes what an unauthenticated session can read. Every result should be zero rows: `anon`
 * reaches nothing tenant-owned (database.md S32), and even reference tables scope their read
 * policy to `authenticated, flowza_app`. A non-zero count here is a finding, not a success.
 */
export async function probeAnonymousReach(): Promise<Probe[]> {
  if (!url || !anonKey) return []
  // A client of its own, with NO session: the shared client carries whoever is signed in, and a
  // probe that ran as them would report their own legitimate reads as leaks (reference tables are
  // readable by `authenticated` on purpose). This one holds only the publishable key, which is the
  // visitor the page claims to be.
  const supabase = createClient(url, anonKey, {
    auth: { persistSession: false, autoRefreshToken: false, detectSessionInUrl: false },
  })

  const tables = [
    { table: 'organizations', note: 'tenant root — must be unreachable' },
    { table: 'org_memberships', note: 'tenancy binding — must be unreachable' },
    { table: 'locales', note: 'reference data — scoped to authenticated' },
  ]

  return Promise.all(
    tables.map(async ({ table, note }) => {
      const { count, error } = await supabase
        .from(table)
        .select('*', { count: 'exact', head: true })
      // A policy denial surfaces as zero rows rather than an error; a genuine transport or
      // configuration failure surfaces as an error, and those are different things.
      return {
        table,
        reachable: !error,
        rows: count ?? 0,
        note: error ? `blocked: ${error.message}` : note,
      }
    }),
  )
}

/**
 * Which external sign-in providers this project actually has switched on (ADR 0093).
 *
 * `/auth/v1/settings` is Supabase's own public description of the project's auth configuration —
 * the same answer its client library reads, carrying no secret and nothing tenant-specific. The
 * login page asks it so that a "Continue with Microsoft" button is offered ONLY where the click can
 * succeed. The alternative was a build variable, which is a second place to get wrong: a flag set
 * without the provider gives a door that always fails, and a provider enabled without the flag
 * gives a working door nobody can see until the next deploy. This way the button appears the moment
 * the provider is turned on, and disappears the moment it is turned off.
 *
 * A failure answers `null`, not an empty map. "We could not ask" and "nothing is enabled" are
 * different states and the caller renders them differently: no external door, and no claim that
 * there is none.
 *
 * Cached for the life of the page. The answer changes when somebody edits the project, which is not
 * something a signed-out login screen needs to poll for.
 */
export type ExternalProviders = Readonly<Record<string, boolean>>

type AuthSettings = {
  readonly external?: Record<string, unknown>
  readonly saml_enabled?: unknown
}

/**
 * ONE fetch of the settings document, shared by every question asked of it.
 *
 * `external` and `saml_enabled` live in the same response, so two callers asking two questions is
 * still one request. Cached for the life of the page: the answer changes when somebody edits the
 * project, which is not something a console screen needs to poll for.
 */
let settingsPromise: Promise<AuthSettings | null> | null = null

function authSettings(): Promise<AuthSettings | null> {
  if (!url || !anonKey) return Promise.resolve(null)
  settingsPromise ??= fetch(`${url}/auth/v1/settings`, { headers: { apikey: anonKey } })
    .then(async (response) => (response.ok ? ((await response.json()) as AuthSettings) : null))
    .catch(() => null)
  return settingsPromise
}

export function externalProviders(): Promise<ExternalProviders | null> {
  return authSettings().then((body) => {
    const external = body?.external
    if (typeof external !== 'object' || external === null) return null
    const flags: Record<string, boolean> = {}
    for (const [name, value] of Object.entries(external)) flags[name] = value === true
    return flags
  })
}

/**
 * Whether SAML 2.0 is switched on for this deployment's auth project, or null if we could not ask.
 *
 * This is the OTHER half of single sign-on being usable, and it is the half nothing used to
 * report. Registering an identity provider is a Management API call the Worker makes; whether the
 * project will actually honour a SAML sign-in is a project-level toggle no code here can set —
 * `saml_enabled` lives under `/config/auth`, and `SupabaseSsoAdmin` is deliberately built unable to
 * reach that endpoint, because the same call could rewrite mailer templates (ADR 0062). So it is a
 * dashboard action, on purpose, and it stays one.
 *
 * The failure it exists to make loud: with SAML off, the wizard still registers providers happily
 * and every resulting sign-in fails with "that email domain is not set up for single sign-on
 * here" — the SAME message a genuinely unregistered domain gets. An administrator reading that has
 * no way to tell "you did not finish setting this up" from "we did not finish setting this up",
 * and the runbook's own note on the step is that nothing warns you.
 *
 * Read in the browser rather than through our API because it needs no secret of ours: this is
 * Supabase's public description of its own project, the same document `externalProviders()` already
 * reads for the Microsoft button, carrying the publishable key the page holds anyway. Asking the
 * Worker instead would mean giving the Worker a Supabase key it has no other use for.
 *
 * `null` is "could not ask" and is NOT rendered as a warning. A console that cries misconfiguration
 * because a fetch failed is worse than one that says nothing.
 */
export function samlEnabled(): Promise<boolean | null> {
  return authSettings().then((body) => {
    if (body === null || !('saml_enabled' in body)) return null
    return body.saml_enabled === true
  })
}

/** Test seam: the cache is module state, and a test that asked twice would get the first answer. */
export function resetExternalProvidersCache(): void {
  settingsPromise = null
}
